package fr.frux.core.network.error

import fr.frux.core.common.auth.UnauthorizedException
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.ConflictReason
import fr.frux.core.common.error.InvalidReason
import fr.frux.shared.dto.ErrorResponse
import io.ktor.client.call.body
import io.ktor.client.plugins.ResponseException
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.ContentConvertException
import kotlinx.serialization.SerializationException

/**
 * Traduit l'echec d'un appel API en categorie presentable (#122).
 *
 * Le repli sur [AppError.Network] est deliberé. Une panne de connexion n'a pas de type commun a
 * toutes les cibles : Kotlin/JS ne voit qu'un `Throwable` dont le message est « Failed to fetch »,
 * la JVM une `IOException`, Darwin autre chose encore. Classer par type ce qui n'en a pas ferait
 * retomber le cas le plus frequent — backend injoignable — dans « erreur inattendue ». Un bug
 * client qui remonterait par ce chemin est donc annonce comme un probleme de reseau ; sa cause
 * reste intacte dans les logs, ou elle est de toute facon lue.
 */
suspend fun Throwable.toAppError(): AppError =
    when {
        this is AppError -> this

        this is UnauthorizedException -> AppError.Unauthorized(this)

        this is ResponseException -> toHttpAppError()

        // La reponse est arrivee mais ne correspond pas au contrat : rien a voir avec le reseau.
        // Avec le vrai HttpClient, ContentNegotiation enveloppe l'echec de decodage dans
        // JsonConvertException (sous-type de ContentConvertException), pas SerializationException —
        // sans cette branche, un bug de contrat s'annoncait « probleme de connexion » avec un retry
        // inutile (#211). La branche SerializationException reste pour les usages directs de
        // kotlinx.serialization hors client HTTP.
        this is ContentConvertException -> AppError.Unexpected(this)

        this is SerializationException -> AppError.Unexpected(this)

        else -> AppError.Network(this)
    }

/** Codes 429 de budget/quota journalier (#273/#274/#233/#437), par opposition au rythme (#24). */
private val QUOTA_CODES =
    setOf(
        "OTP_BUDGET_EXHAUSTED",
        "AUTHOR_PHOTO_QUOTA",
        "REPORT_QUOTA_EXCEEDED",
        "SUGGESTION_QUOTA_EXCEEDED",
        "FEEDBACK_LIMIT",
    )

/** Code stable du refus de connexion d'un compte suspendu (#341, ADR 0023 lot 3). */
private const val ACCOUNT_SUSPENDED_CODE = "ACCOUNT_SUSPENDED"

/** Code stable du refus d'une fonction réservée à Frux+ (#456, ADR 0016). */
private const val PREMIUM_REQUIRED_CODE = "PREMIUM_REQUIRED"

/** Code stable du refus d'un déplacement au-delà de la distance permise (spec 2026-10-19). */
private const val MOVE_TOO_FAR_CODE = "MOVE_TOO_FAR"

/**
 * Code stable du refus de masquer ou signaler un compte système d'import, `ADMIN` ou de review
 * Play (#515, ADR 0029) — `UserModerationRoutes.kt` côté serveur. Sans ce cas, le 409 générique
 * affichait « Cette action a déjà été prise en compte », un message trompeur : le geste n'a jamais
 * eu lieu, il est refusé.
 */
private const val TARGET_NOT_BLOCKABLE_CODE = "TARGET_NOT_BLOCKABLE"

/** Libellé dédié du refus ci-dessus — chaîne métier déjà formulée côté client, pas de ressource
 *  Compose ici : `core-network` est aussi compilé hors UI (cf. doc de [AppError.Conflict]). */
private const val TARGET_NOT_BLOCKABLE_MESSAGE = "Ce compte ne peut pas être masqué ni signalé."

private const val ATTACH_FORBIDDEN_CODE = "ATTACH_FORBIDDEN"
private const val SELF_REPORT_CODE = "SELF_REPORT"
private const val SITE_PRIVATE_CODE = "SITE_PRIVATE"
private const val NOT_SITE_OWNER_CODE = "NOT_SITE_OWNER"

/** Codes 409 des lieux de cueillette (ADR 0035), un par [ConflictReason]. */
private fun conflictReasonOf(code: String?): ConflictReason? = ConflictReason.entries.firstOrNull { it.name == code }

/**
 * Code stable du corps `ErrorResponse`, ou null si le corps est absent ou d'une autre forme.
 * Lecture tolerante : un 429 nu du plugin RateLimit n'a pas toujours traverse StatusPages.
 */
private suspend fun ResponseException.errorCode(): String? {
    val body = runCatching { response.body<ErrorResponse>() }.getOrNull()
    return body?.code
}

/** Cause métier d'un 400 reconnue par son code stable, `null` pour tout autre refus. */
private suspend fun ResponseException.badRequestReason(): InvalidReason? =
    if (response.status == HttpStatusCode.BadRequest && errorCode() == MOVE_TOO_FAR_CODE) {
        InvalidReason.MOVE_TOO_FAR
    } else {
        null
    }

/**
 * 403 : causes métier connues, compte suspendu (#341), fonction Frux+ (#456) et lieux (#617). Tout autre
 * 403 reste un Invalid nu, comme avant, la lecture du corps est tolérante, un corps absent rend null.
 */
private suspend fun ResponseException.forbiddenReason(): InvalidReason? =
    when (errorCode()) {
        ACCOUNT_SUSPENDED_CODE -> InvalidReason.ACCOUNT_SUSPENDED
        PREMIUM_REQUIRED_CODE -> InvalidReason.PREMIUM_REQUIRED
        ATTACH_FORBIDDEN_CODE -> InvalidReason.ATTACH_FORBIDDEN
        SELF_REPORT_CODE -> InvalidReason.SELF_REPORT
        SITE_PRIVATE_CODE -> InvalidReason.SITE_PRIVATE
        NOT_SITE_OWNER_CODE -> InvalidReason.NOT_SITE_OWNER
        else -> null
    }

private suspend fun ResponseException.toHttpAppError(): AppError =
    when (val status = response.status) {
        HttpStatusCode.Unauthorized -> {
            AppError.Unauthorized(this)
        }

        HttpStatusCode.NotFound -> {
            AppError.NotFound(this)
        }

        HttpStatusCode.Conflict -> {
            val code = errorCode()
            val override = TARGET_NOT_BLOCKABLE_MESSAGE.takeIf { code == TARGET_NOT_BLOCKABLE_CODE }
            AppError.Conflict(this, userMessageOverride = override, reason = conflictReasonOf(code))
        }

        HttpStatusCode.Forbidden -> {
            AppError.Invalid(this, forbiddenReason())
        }

        // Avant #24 ce 429 tombait dans `Invalid`, donc sur « verifie les informations saisies » —
        // un conseil faux : la saisie est bonne, c'est le rythme qui est refuse. La categorie porte
        // le delai annonce par `Retry-After` quand le serveur en donne un, et distingue les 429 de
        // budget journalier (#291) : « patiente un instant » serait faux, la fenetre est le lendemain.
        HttpStatusCode.TooManyRequests -> {
            AppError.RateLimited(
                retryAfterSeconds = response.headers[HttpHeaders.RetryAfter]?.toLongOrNull(),
                cause = this,
                quotaExhausted = errorCode() in QUOTA_CODES,
            )
        }

        else -> {
            when {
                status.value in 500..599 -> AppError.Server(status.value, this)
                status.value in 400..499 -> AppError.Invalid(this, badRequestReason())
                else -> AppError.Unexpected(this)
            }
        }
    }

/** Convertit l'echec d'un `Result` de transport en [AppError], en laissant le succes intact. */
suspend fun <T> Result<T>.mapFailureToAppError(): Result<T> =
    fold(onSuccess = { Result.success(it) }, onFailure = { Result.failure(it.toAppError()) })
